// Client Authentication Service: Zero-Cleartext Password Management & Edge Session Sync

const DEFAULT_SALT = 'starlife_simulator_secure_2026_salt';
// Cryptographic Salted SHA-256 Hash of the admin master password (NEVER stores cleartext)
const EXPECTED_HASH = '0ca3d7b12a59d2f6c89095217bed1707e0d0487781c584078517af4bc5ecc581';
const STORAGE_KEY = 'sl_auth_session_token';
const EXPIRY_KEY = 'sl_auth_session_exp';
const USER_KEY = 'sl_auth_user';

export interface AuthUser {
  accountId: string;
  name: string;
  role: 'admin' | 'consultant';
}

export interface AuthResult {
  success: boolean;
  error?: string;
  expiresAt?: number;
  user?: AuthUser;
}

function toBase64Utf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64Utf8(base64: string): string {
  let normalized = base64.replace(/-/g, '+').replace(/_/g, '/');
  while (normalized.length % 4 !== 0) {
    normalized += '=';
  }
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

async function sha256Hex(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function hmacSha256(keyStr: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(keyStr),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
}

const memoryStore = new Map<string, string>();

function getStorageItem(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.getItem === 'function') {
      const val = localStorage.getItem(key);
      if (val !== null) return val;
    }
  } catch {
    // Ignore
  }
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage && typeof sessionStorage.getItem === 'function') {
      const val = sessionStorage.getItem(key);
      if (val !== null) return val;
    }
  } catch {
    // Ignore
  }
  return memoryStore.get(key) || null;
}

function setStorageItem(key: string, value: string, remember: boolean = true) {
  memoryStore.set(key, value);
  try {
    if (remember && typeof localStorage !== 'undefined' && localStorage && typeof localStorage.setItem === 'function') {
      localStorage.setItem(key, value);
    } else if (!remember && typeof sessionStorage !== 'undefined' && sessionStorage && typeof sessionStorage.setItem === 'function') {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Ignore storage quota/disabled errors
  }
}

function removeStorageItem(key: string) {
  memoryStore.delete(key);
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.removeItem === 'function') {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore
  }
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage && typeof sessionStorage.getItem === 'function') {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore
  }
}

/**
 * Check if the current user has a valid active session.
 */
export function checkStoredAuthSession(): boolean {
  try {
    const token = getStorageItem(STORAGE_KEY);
    const expStr = getStorageItem(EXPIRY_KEY);
    if (!token || !expStr) return false;

    const exp = parseInt(expStr, 10);
    if (isNaN(exp) || Date.now() > exp) {
      clearAuthSession();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Get current session token for API authorization
 */
export function getCurrentSessionToken(): string | null {
  if (!checkStoredAuthSession()) return null;
  return getStorageItem(STORAGE_KEY);
}

/**
 * Retrieve current authenticated user profile
 */
export function getStoredUser(): AuthUser | null {
  try {
    const raw = getStorageItem(USER_KEY);
    if (!raw) {
      // Reconstruct from token if stored (backward compatibility)
      const token = getStorageItem(STORAGE_KEY);
      if (token && checkStoredAuthSession()) {
        try {
          const parts = token.split('.');
          if (parts.length >= 1) {
            const parsed = JSON.parse(fromBase64Utf8(parts[0]));
            if (parsed.accountId) {
              return {
                accountId: parsed.accountId,
                name: parsed.name || (parsed.role === 'admin' ? 'Сергій (Адміністратор)' : 'Консультант'),
                role: parsed.role || 'consultant',
              };
            }
          }
        } catch {
          // Fallback
        }
      }
      return null;
    }
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * Persist authenticated session token and user profile
 */
export function saveAuthSession(
  token: string,
  expiresAt: number,
  remember: boolean = true,
  user?: AuthUser
) {
  setStorageItem(STORAGE_KEY, token, remember);
  setStorageItem(EXPIRY_KEY, expiresAt.toString(), remember);
  if (user) {
    setStorageItem(USER_KEY, JSON.stringify(user), remember);
  }
}

/**
 * Clear current authentication session (Logout / Lock)
 */
export function clearAuthSession() {
  removeStorageItem(STORAGE_KEY);
  removeStorageItem(EXPIRY_KEY);
  removeStorageItem(USER_KEY);

  // Notify server to clear HTTP-only cookie if available
  try {
    if (typeof fetch === 'function') {
      fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logout' }),
      }).catch(() => {});
    }
  } catch {
    // Ignore
  }
}

/**
 * Authenticate with password:
 * 1. Attempts Cloudflare Pages Function endpoint POST /api/auth
 * 2. Falls back to client-side zero-cleartext salted WebCrypto SHA-256 verification (for offline/unit tests)
 */
export async function authenticateWithPassword(password: string, remember: boolean = true): Promise<AuthResult> {
  const trimmed = (password || '').trim();
  if (!trimmed) {
    return { success: false, error: 'Будь ласка, введіть пароль доступу' };
  }

  // 1. Try server-side Cloudflare Pages Function
  try {
    const res = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: trimmed }),
    });

    if (res.ok) {
      const data = (await res.json()) as {
        success: boolean;
        token?: string;
        expiresAt?: number;
        user?: AuthUser;
      };
      if (data.success && data.token && data.expiresAt) {
        const user: AuthUser = data.user || {
          accountId: 'admin_sergey',
          name: 'Сергій (Адміністратор)',
          role: 'admin',
        };
        saveAuthSession(data.token, data.expiresAt, remember, user);
        return { success: true, expiresAt: data.expiresAt, user };
      }
    } else if (res.status === 401 || res.status === 403) {
      const errData = (await res.json().catch(() => ({}))) as { error?: string };
      return {
        success: false,
        error: errData.error || (res.status === 403 ? 'Доступ заблоковано адміністратором' : 'Невірний пароль доступу'),
      };
    }
  } catch {
    // If running in local Vite dev without Pages Functions or offline, continue to fallback
  }

  // 2. Client-side Cryptographic Salted SHA-256 verification (Zero-Cleartext in Bundle)
  try {
    const computedHash = await sha256Hex(`${DEFAULT_SALT}:${trimmed}`);
    const isMaster = constantTimeEqual(computedHash, EXPECTED_HASH);

    if (!isMaster) {
      return { success: false, error: 'Невірний пароль доступу' };
    }

    const expiresAt = Date.now() + (remember ? 14 * 24 * 3600 * 1000 : 24 * 3600 * 1000);
    const user: AuthUser = {
      accountId: 'admin_sergey',
      name: 'Сергій (Адміністратор)',
      role: 'admin',
    };

    const sessionPayload = {
      authenticated: true,
      accountId: user.accountId,
      name: user.name,
      role: user.role,
      iat: Date.now(),
      exp: expiresAt,
    };
    const payloadStr = JSON.stringify(sessionPayload);
    const encodedPayload = toBase64Utf8(payloadStr);
    const signature = await hmacSha256(DEFAULT_SALT, encodedPayload);
    const validToken = `${encodedPayload}.${signature}`;
    saveAuthSession(validToken, expiresAt, remember, user);

    return { success: true, expiresAt, user };
  } catch {
    return { success: false, error: 'Помилка криптографічної перевірки' };
  }
}

/**
 * Verify session validity with Cloudflare Pages Function endpoint GET /api/auth.
 * If server returns authenticated: false (or 401/403, e.g. blocked or deleted account in KV),
 * clears local session and returns false.
 */
export async function verifyServerSession(): Promise<boolean> {
  const token = getCurrentSessionToken();
  if (!token) return false;

  try {
    const res = await fetch('/api/auth', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.ok) {
      const data = await res.json();
      if (data.authenticated) {
        if (data.user) {
          setStorageItem(USER_KEY, JSON.stringify(data.user));
        }
        return true;
      }
      clearAuthSession();
      return false;
    } else if (res.status === 401 || res.status === 403) {
      clearAuthSession();
      return false;
    }
  } catch {
    // Offline or network error: retain local stored session
    return checkStoredAuthSession();
  }
  return checkStoredAuthSession();
}
